import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PhoneNumberUtil } from "google-libphonenumber";
import type { TFunction } from "i18next";
import { z } from "zod";

const phoneUtil = PhoneNumberUtil.getInstance();

const requiredValidator = (t: TFunction, name: string) =>
  z.string().min(1, t(`fields:${name}.error.required`));

const emailValidator = (t: TFunction, name: string = "email") =>
  z.string().email(t(`fields:${name}.error.invalid`));

const phoneValidator = (t: TFunction, name: string = "phone") =>
  z
    .string()
    .min(1, t(`fields:${name}.error.required`))
    .refine(
      (value) => {
        if (value.length === 0) {
          return true;
        } // Let min(1) handle empty string
        try {
          const phoneNumber = phoneUtil.parse(value);
          return phoneUtil.isValidNumber(phoneNumber);
        } catch {
          return false;
        }
      },
      {
        message: t(`fields:${name}.error.invalid`),
      },
    );

export const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

const timeValidator = (t: TFunction, name: string = "time") =>
  z.string().regex(TIME_REGEX, t(`fields:${name}.error.invalid`));

const passwordValidator = (t: TFunction, minLength: number, name: string = "password") =>
  z
    .string()
    .min(1, t(`fields:${name}.error.required`))
    // Twee regels achter elkaar, en niet één `.min(minLength)`: een leeg veld is
    // "verplicht" en niet "te kort". Zod meldt de checks in deze volgorde, dus de
    // eerste boodschap is de juiste.
    .min(minLength, t(`fields:${name}.error.min-length`, { number: minLength }));

export const Validator = {
  required: requiredValidator,
  email: emailValidator,
  phone: phoneValidator,
  time: timeValidator,
  password: passwordValidator,
};

const ValidatorHook = (t: TFunction) => ({
  required: (options: { name: string; schema?: z.ZodString }) =>
    (options?.schema ?? z.string()).min(1, t(`fields:${options.name}.error.required`)),
  email: (options?: { name?: string; schema?: z.ZodString }) =>
    (options?.schema ?? z.string()).email(t(`fields:${options?.name ?? "email"}.error.invalid`)),
  phone: (options?: { name?: string; schema?: z.ZodString }) =>
    (options?.schema ?? z.string()).refine(
      (value) => {
        try {
          const phoneNumber = phoneUtil.parse(value);
          return phoneUtil.isValidNumber(phoneNumber);
        } catch {
          return false;
        }
      },
      {
        message: t(`fields:${options?.name ?? "phone"}.error.invalid`),
      },
    ),
  time: (options?: { name?: string; schema?: z.ZodString }) =>
    (options?.schema ?? z.string()).regex(
      TIME_REGEX,
      t(`fields:${options?.name ?? "time"}.error.invalid`),
    ),
  password: (options: { minLength: number; name?: string }) =>
    passwordValidator(t, options.minLength, options.name ?? "password"),
});

export function useValidator() {
  const { t } = useTranslation();

  return useMemo(() => ValidatorHook(t), [t]);
}
